import { Loader2, MapPin } from 'lucide-react';
import { useEffect, useId, useMemo, useRef, useState } from 'react';

import { Input } from '@/components/ui/input';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import {
  AdminApiError,
  adminApi,
  type GeocodeAddress,
  type GeocodeSuggestion,
} from '@/lib/admin-api';
import { cn } from '@/lib/utils';

/**
 * A street-address field with Mapbox suggestions.
 *
 * Still a plain text input underneath, so an address the search has never
 * heard of can be typed and saved -- and so the form keeps working when the
 * API answers 503 for a missing token. Picking a suggestion calls
 * `retrieve`, which is what yields the exact point `find_delivery_zones`
 * needs; the suggestions themselves carry no coordinates.
 *
 * One `sessionToken` per mount ties the typing session's suggests to its
 * retrieve, which is how Mapbox bills it as a single session.
 *
 * Used by the order editor, the customer address book and the delivery-zone
 * settings, so every place staff type an address behaves the same way.
 */
export function AddressAutocomplete({
  id,
  value,
  onChange,
  onResolved,
  proximity,
  placeholder = 'Start typing an address',
  disabled,
  className,
}: {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  /** Fired with the full, geocoded address after a suggestion is chosen. */
  onResolved: (address: GeocodeAddress) => void;
  /** The store, to bias results toward its neighbourhood. */
  proximity?: { latitude: number; longitude: number } | null;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}) {
  const generatedId = useId();
  const inputId = id ?? generatedId;

  const sessionToken = useMemo(() => crypto.randomUUID(), []);

  const [suggestions, setSuggestions] = useState<GeocodeSuggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [highlight, setHighlight] = useState(0);

  // Only a value the operator typed triggers a search; a value we set from a
  // retrieved suggestion must not re-open the list under their cursor.
  const typed = useRef(false);
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!typed.current || unavailable) return;

    const term = value.trim();

    if (term.length < 3) {
      setSuggestions([]);
      setOpen(false);
      return;
    }

    if (debounce.current) clearTimeout(debounce.current);

    debounce.current = setTimeout(async () => {
      setBusy(true);

      try {
        const results = await adminApi.geocode.search({
          q: term,
          sessionToken,
          proximity: proximity ? `${proximity.longitude},${proximity.latitude}` : undefined,
        });

        setSuggestions(results);
        setHighlight(0);
        setOpen(results.length > 0);
      } catch (error) {
        // No token on the worker: degrade to a plain field for this mount.
        if (error instanceof AdminApiError && error.status === 503) {
          setUnavailable(true);
        }

        setSuggestions([]);
        setOpen(false);
      } finally {
        setBusy(false);
      }
    }, 250);

    return () => {
      if (debounce.current) clearTimeout(debounce.current);
    };
  }, [value, sessionToken, proximity, unavailable]);

  async function choose(suggestion: GeocodeSuggestion) {
    setOpen(false);
    setBusy(true);

    try {
      const address = await adminApi.geocode.retrieve({ id: suggestion.id, sessionToken });

      typed.current = false;
      onChange(address.line1);
      onResolved(address);
    } catch {
      // Leave what they typed; the form still saves a hand-entered address.
    } finally {
      setBusy(false);
    }
  }

  return (
    <Popover open={open && !disabled} onOpenChange={setOpen}>
      <PopoverAnchor asChild>
        <div className={cn('relative', className)}>
          <Input
            id={inputId}
            value={value}
            placeholder={placeholder}
            disabled={disabled}
            autoComplete="off"
            role="combobox"
            aria-expanded={open}
            aria-controls={`${inputId}-listbox`}
            aria-autocomplete="list"
            onChange={(event) => {
              typed.current = true;
              onChange(event.target.value);
            }}
            onFocus={() => {
              if (suggestions.length) setOpen(true);
            }}
            onKeyDown={(event) => {
              if (!open) return;

              if (event.key === 'ArrowDown') {
                event.preventDefault();
                setHighlight((current) => Math.min(current + 1, suggestions.length - 1));
              } else if (event.key === 'ArrowUp') {
                event.preventDefault();
                setHighlight((current) => Math.max(current - 1, 0));
              } else if (event.key === 'Enter') {
                event.preventDefault();
                const chosen = suggestions[highlight];
                if (chosen) void choose(chosen);
              } else if (event.key === 'Escape') {
                setOpen(false);
              }
            }}
            className="pr-8"
          />

          <span className="text-muted-foreground pointer-events-none absolute inset-y-0 right-2.5 flex items-center">
            {busy ? <Loader2 className="size-4 animate-spin" /> : <MapPin className="size-4" />}
          </span>
        </div>
      </PopoverAnchor>

      <PopoverContent
        align="start"
        className="w-[var(--radix-popper-anchor-width)] p-1"
        // Keep focus in the input: the list is navigated with the arrows.
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <div id={`${inputId}-listbox`} role="listbox" className="flex flex-col">
          {suggestions.map((suggestion, index) => (
            <div key={suggestion.id}>
              <button
                type="button"
                role="option"
                aria-selected={index === highlight}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => void choose(suggestion)}
                onMouseEnter={() => setHighlight(index)}
                className={cn(
                  'flex w-full flex-col items-start rounded-sm px-2 py-1.5 text-left text-sm',
                  index === highlight && 'bg-accent text-accent-foreground',
                )}
              >
                <span className="font-medium">{suggestion.name}</span>
                {suggestion.placeFormatted && (
                  <span className="text-muted-foreground text-xs">{suggestion.placeFormatted}</span>
                )}
              </button>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
